import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Typography, IconButton, Tooltip as MuiTooltip, Collapse, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import FilterListIcon from '@mui/icons-material/FilterList';
import { useSelector } from 'react-redux';
import { Chart as ChartJS, CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend } from 'chart.js';
import { Bar } from 'react-chartjs-2';
import { bloqcibeApi, useGetBalancingCriteriaConfigQuery, useGetTrialDetailsV2Query } from '../../../store/slices/apiSlice';
import AnimatedBloqcubeLogo from '../../../components/Logo/AnimatedBloqcubeLogo';
import { SUBJECT_STATUSES } from '../../../util/constants';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Colors
const EXCEEDED_COLOR = { border: '#F44336', background: 'rgba(244, 67, 54, 0.7)' };

// Color palette for sites (12 colors)
const SITE_COLORS = [
  { border: '#2196F3', background: 'rgba(33, 150, 243, 0.7)' },   // Blue
  { border: '#4CAF50', background: 'rgba(76, 175, 80, 0.7)' },    // Green
  { border: '#FF9800', background: 'rgba(255, 152, 0, 0.7)' },    // Orange
  { border: '#9C27B0', background: 'rgba(156, 39, 176, 0.7)' },   // Purple
  { border: '#00BCD4', background: 'rgba(0, 188, 212, 0.7)' },    // Cyan
  { border: '#795548', background: 'rgba(121, 85, 72, 0.7)' },    // Brown
  { border: '#607D8B', background: 'rgba(96, 125, 139, 0.7)' },   // Blue Grey
  { border: '#E91E63', background: 'rgba(233, 30, 99, 0.7)' },    // Pink
  { border: '#3F51B5', background: 'rgba(63, 81, 181, 0.7)' },    // Indigo
  { border: '#009688', background: 'rgba(0, 150, 136, 0.7)' },    // Teal
  { border: '#CDDC39', background: 'rgba(205, 220, 57, 0.7)' },   // Lime
  { border: '#FF5722', background: 'rgba(255, 87, 34, 0.7)' },    // Deep Orange
];

/**
 * Calculate target number based on distribution type
 */
const calculateTargetNumber = (targetValue, distribution, totalSubjects) => {
  const isPercentageDistribution = distribution === '%';
  if (isPercentageDistribution) {
    return totalSubjects > 0 ? Math.floor((targetValue / 100) * totalSubjects) : 0;
  }
  return targetValue;
};

const AdvancedTherapiesUsedWidget = (props) => {
  const { trial: trialId, site: siteIds, siteList = [], documentKey: propDocumentKey } = props;
  const sponsorId = useSelector((state) => state?.userDetails?.loggedInUser.sponsorId);
  const [getMultiSiteSubjectCountByField] = bloqcibeApi.endpoints.getMultiSiteSubjectCountByField.useLazyQuery();
  const [aggregatedData, setAggregatedData] = useState(null);
  const [isLoadingAggregations, setIsLoadingAggregations] = useState(false);

  // Status filter state
  const [showFilters, setShowFilters] = useState(false);
  const [includeStatuses, setIncludeStatuses] = useState({
    // CRF Pending statuses - included by default
    [SUBJECT_STATUSES.PENDING]: true,
    [SUBJECT_STATUSES.PENDING_VERIFICATION]: true,
    [SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]: true,
    // Other statuses - excluded by default
    [SUBJECT_STATUSES.COMPLETED]: true,
    [SUBJECT_STATUSES.ON_HOLD]: false,
    [SUBJECT_STATUSES.SCREENING_FAILURE]: false,
    [SUBJECT_STATUSES.ENROLLMENT_FAILURE]: false,
    [SUBJECT_STATUSES.WITHDRAWAL]: false,
  });

  // Helper to handle grouped CRF Pending filter toggle
  const handleCRFPendingFilterChange = useCallback(() => {
    setIncludeStatuses((prev) => {
      const newValue = !(prev[SUBJECT_STATUSES.PENDING] && 
                         prev[SUBJECT_STATUSES.PENDING_VERIFICATION] && 
                         prev[SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]);
      return {
        ...prev,
        [SUBJECT_STATUSES.PENDING]: newValue,
        [SUBJECT_STATUSES.PENDING_VERIFICATION]: newValue,
        [SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]: newValue,
      };
    });
  }, []);

  // Check if all CRF Pending statuses are included
  const isCRFPendingIncluded = useMemo(() => {
    return includeStatuses[SUBJECT_STATUSES.PENDING] && 
           includeStatuses[SUBJECT_STATUSES.PENDING_VERIFICATION] && 
           includeStatuses[SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION];
  }, [includeStatuses]);

  // Build status filter for API call
  const statusFilter = useMemo(() => {
    const excludeStatusArray = Object.entries(includeStatuses)
      .filter(([_, isIncluded]) => !isIncluded)
      .map(([status]) => status);
    
    return excludeStatusArray.length > 0 
      ? { excludeStatuses: excludeStatusArray } 
      : undefined;
  }, [includeStatuses]);

  // Handle status filter change
  const handleStatusFilterChange = useCallback((status) => {
    setIncludeStatuses((prev) => ({
      ...prev,
      [status]: !prev[status],
    }));
  }, []);

  // Default filter values - used to determine if filters have been changed
  const defaultFilterValues = useMemo(() => ({
    [SUBJECT_STATUSES.PENDING]: true,
    [SUBJECT_STATUSES.PENDING_VERIFICATION]: true,
    [SUBJECT_STATUSES.PENDING_EXTERNAL_VERIFICATION]: true,
    [SUBJECT_STATUSES.COMPLETED]: true,
    [SUBJECT_STATUSES.ON_HOLD]: false,
    [SUBJECT_STATUSES.SCREENING_FAILURE]: false,
    [SUBJECT_STATUSES.ENROLLMENT_FAILURE]: false,
    [SUBJECT_STATUSES.WITHDRAWAL]: false,
  }), []);

  // Count how many filters differ from their default values
  const changedFilterCount = useMemo(() => {
    return Object.entries(includeStatuses).filter(
      ([status, isIncluded]) => isIncluded !== defaultFilterValues[status]
    ).length;
  }, [includeStatuses, defaultFilterValues]);

  const hasValidIds = Boolean(sponsorId && trialId);

  const { data: trialDetails, isLoading: isLoadingTrial } = useGetTrialDetailsV2Query(
    { sponsorId, id: trialId },
    { skip: !hasValidIds }
  );

  const documentKey = useMemo(
    () => propDocumentKey || trialDetails?.trialData?.eConsentForm,
    [propDocumentKey, trialDetails]
  );

  const { data: configData, isLoading: isLoadingConfig } = useGetBalancingCriteriaConfigQuery(
    { type: 'SUBJECT_BALANCING_CRITERIA', documentKey },
    { skip: !documentKey }
  );

  const criteriaConfig = useMemo(() => {
    if (!configData?.metaData) return null;
    const metaData = configData.metaData;
    const config = metaData.default || {};
    return config.ADVANCED_THERAPIES_USED || null;
  }, [configData]);

  useEffect(() => {
    const fetchAggregations = async () => {
      if (!criteriaConfig || !sponsorId || !trialId || !siteIds?.length) return;

      setIsLoadingAggregations(true);
      try {
        const fieldsToQuery = criteriaConfig.fields.map((field) => ({
          stepKey: configData.stepKey,
          sectionKey: configData.sectionKey,
          subSectionKey: configData.subSectionKey || null,
          categoryKey: field.categoryKey,
          fieldKey: field.fieldKey,
        }));

        const result = await getMultiSiteSubjectCountByField({
          sponsorId,
          trialId,
          body: {
            siteIds,
            fields: fieldsToQuery,
            statusFilter: statusFilter,
          },
        }).unwrap();

        setAggregatedData(result);
      } catch (error) {
        console.error('Error fetching aggregation:', error);
        setAggregatedData({ sites: [], aggregated: [] });
      } finally {
        setIsLoadingAggregations(false);
      }
    };

    fetchAggregations();
  }, [criteriaConfig, sponsorId, trialId, siteIds, configData, getMultiSiteSubjectCountByField, statusFilter]);

  // Build value configs for labels and target info
  const valueConfigs = useMemo(() => {
    if (!criteriaConfig?.fields) return [];
    const configs = [];
    criteriaConfig.fields.forEach((field) => {
      if (field.values) {
        field.values.forEach((valueConfig) => {
          configs.push({
            label: valueConfig.label || valueConfig.option,
            option: valueConfig.option,
            target: valueConfig.value || 0,
            distribution: valueConfig.distribution || valueConfig.disribution || '%',
            showMaximum: valueConfig.showMaximum !== false,
          });
        });
      }
    });
    return configs;
  }, [criteriaConfig]);

  const labels = useMemo(() => {
    return valueConfigs.map((vc) => vc.label);
  }, [valueConfigs]);

  const { datasets, targetInfo } = useMemo(() => {
    if (!aggregatedData || !criteriaConfig?.fields) {
      return { datasets: [], targetInfo: [] };
    }

    const isMultiSite = siteIds?.length > 1;
    const allDatasets = [];

    if (isMultiSite && aggregatedData.sites?.length > 0) {
      // Multi-site: create stacked datasets per site
      aggregatedData.sites.forEach((siteData, siteIndex) => {
        const colorIndex = siteIndex % SITE_COLORS.length;
        const baseColor = SITE_COLORS[colorIndex];

        const siteInfo = siteList.find((s) => s.id === siteData.siteId);
        const siteName = siteInfo?.siteName || `Site ${siteData.siteId}`;
        const totalSubjects = siteInfo?.totalSubjects || 0;

        const withinTargetCounts = [];
        const exceededCounts = [];
        const targetNumbers = [];
        const actualCounts = [];

        criteriaConfig.fields.forEach((field) => {
          if (field.values) {
            const fieldData = siteData.fields?.find(
              (f) => f.fieldKey === field.fieldKey && f.categoryKey === field.categoryKey
            );
            const countsMap = {};
            if (fieldData?.counts) {
              fieldData.counts.forEach(({ value, subjectCount }) => {
                countsMap[value] = subjectCount;
              });
            }
            field.values.forEach((valueConfig) => {
              const current = countsMap[valueConfig.option] || 0;
              const distribution = valueConfig.distribution || valueConfig.disribution || '%';
              const targetNumber = calculateTargetNumber(valueConfig.value || 0, distribution, totalSubjects);
              const showMaximum = valueConfig.showMaximum !== false;

              actualCounts.push(current);
              targetNumbers.push(targetNumber);

              if (showMaximum && targetNumber > 0) {
                withinTargetCounts.push(Math.min(current, targetNumber));
                exceededCounts.push(Math.max(0, current - targetNumber));
              } else {
                withinTargetCounts.push(current);
                exceededCounts.push(0);
              }
            });
          }
        });

        // Within target dataset
        allDatasets.push({
          label: siteName,
          data: withinTargetCounts,
          backgroundColor: baseColor.background,
          borderColor: baseColor.border,
          stack: `site-${siteIndex}`,
          targetNumbers,
          actualCounts,
          totalSubjects,
        });

        // Exceeded dataset (only if there are exceeded values)
        if (exceededCounts.some((v) => v > 0)) {
          allDatasets.push({
            label: `${siteName} (Exceeded)`,
            data: exceededCounts,
            backgroundColor: EXCEEDED_COLOR.background,
            borderColor: EXCEEDED_COLOR.border,
            stack: `site-${siteIndex}`,
            targetNumbers,
            actualCounts,
            totalSubjects,
            isExceeded: true,
          });
        }
      });
    } else {
      // Single site
      const siteId = siteIds?.[0];
      const siteInfo = siteList.find((s) => s.id === siteId);
      const siteName = siteInfo?.siteName || 'Current';
      const totalSubjects = siteInfo?.totalSubjects || 0;

      const dataSource = aggregatedData.aggregated || aggregatedData;
      const countsMap = {};
      (Array.isArray(dataSource) ? dataSource : []).forEach((fieldData) => {
        if (fieldData.counts) {
          fieldData.counts.forEach(({ value, subjectCount }) => {
            countsMap[value] = (countsMap[value] || 0) + subjectCount;
          });
        }
      });

      const withinTargetCounts = [];
      const exceededCounts = [];
      const targetNumbers = [];
      const actualCounts = [];

      criteriaConfig.fields.forEach((field) => {
        if (field.values) {
          field.values.forEach((valueConfig) => {
            const current = countsMap[valueConfig.option] || 0;
            const distribution = valueConfig.distribution || valueConfig.disribution || '%';
            const targetNumber = calculateTargetNumber(valueConfig.value || 0, distribution, totalSubjects);
            const showMaximum = valueConfig.showMaximum !== false;

            actualCounts.push(current);
            targetNumbers.push(targetNumber);

            if (showMaximum && targetNumber > 0) {
              withinTargetCounts.push(Math.min(current, targetNumber));
              exceededCounts.push(Math.max(0, current - targetNumber));
            } else {
              withinTargetCounts.push(current);
              exceededCounts.push(0);
            }
          });
        }
      });

      // Within target dataset
      allDatasets.push({
        label: siteName,
        data: withinTargetCounts,
        backgroundColor: SITE_COLORS[0].background,
        borderColor: SITE_COLORS[0].border,
        stack: 'single',
        targetNumbers,
        actualCounts,
        totalSubjects,
      });

      // Exceeded dataset
      if (exceededCounts.some((v) => v > 0)) {
        allDatasets.push({
          label: 'Exceeded',
          data: exceededCounts,
          backgroundColor: EXCEEDED_COLOR.background,
          borderColor: EXCEEDED_COLOR.border,
          stack: 'single',
          targetNumbers,
          actualCounts,
          totalSubjects,
          isExceeded: true,
        });
      }
    }

    return { datasets: allDatasets, targetInfo: valueConfigs };
  }, [aggregatedData, criteriaConfig, siteIds, siteList, valueConfigs]);

  // Calculate max target and max actual for Y-axis scaling
  const yAxisMax = useMemo(() => {
    let maxTarget = 0;
    let maxActual = 0;
    
    datasets.forEach((dataset) => {
      if (dataset.targetNumbers) {
        maxTarget = Math.max(maxTarget, ...dataset.targetNumbers);
      }
      if (dataset.actualCounts) {
        maxActual = Math.max(maxActual, ...dataset.actualCounts);
      }
    });
    
    // Use the larger of maxTarget or maxActual, with some padding
    return Math.max(maxTarget, maxActual) + 1;
  }, [datasets]);

  const options = useMemo(() => ({
    indexAxis: 'x',
    elements: {
      bar: {
        borderWidth: 2,
      },
    },
    responsive: true,
    maintainAspectRatio: false,
    maxBarThickness: 50,
    plugins: {
      legend: {
        position: 'bottom',
        labels: {
          filter: (legendItem) => {
            // Hide "Exceeded" legend items as they're part of the stacked bar
            return !legendItem.text.includes('Exceeded');
          },
        },
      },
      datalabels: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        callbacks: {
          label: function (context) {
            const dataset = context.dataset;
            const dataIndex = context.dataIndex;
            
            // Skip exceeded datasets in tooltip (show combined info in main dataset)
            if (dataset.isExceeded) {
              return null;
            }
            
            const actual = dataset.actualCounts?.[dataIndex] || 0;
            const targetNumber = dataset.targetNumbers?.[dataIndex] || 0;
            const config = targetInfo[dataIndex];
            
            const lines = [`${dataset.label}: ${actual}`];
            
            if (config?.showMaximum && targetNumber > 0) {
              lines.push(`Target: ${targetNumber} (${config.target}${config.distribution})`);
              if (actual > targetNumber) {
                lines.push(`⚠️ Exceeded by ${actual - targetNumber}`);
              }
            }
            
            return lines;
          },
        },
      },
    },
    scales: {
      x: {
        stacked: true,
      },
      y: {
        stacked: true,
        beginAtZero: true,
        suggestedMax: yAxisMax,
        ticks: {
          stepSize: 1,
          callback: function (value) {
            if (Number.isInteger(value) || value == 0) {
              return value;
            }
          },
        },
      },
    },
  }), [targetInfo, yAxisMax]);

  const data = {
    labels,
    datasets,
  };

  const isLoading = isLoadingTrial || isLoadingConfig || isLoadingAggregations;

  if (!criteriaConfig && !isLoading) {
    return (
      <Box
        onMouseDown={(e) => e.stopPropagation()}
        sx={{
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          alignItems: 'center',
          height: '100%',
          p: 1,
        }}
      >
        <Typography variant="body2" color="text.secondary">
          Configuration not available
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      onMouseDown={(e) => e.stopPropagation()}
      sx={{ display: 'flex', flexDirection: 'column', height: '100%', p: 1 }}
    >
      {/* Filter Toggle Button */}
      <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 0.5 }}>
        <MuiTooltip title="Filter by subject status">
          <IconButton
            size="small"
            onClick={() => setShowFilters(!showFilters)}
            sx={{
              color: changedFilterCount > 0 ? 'primary.main' : 'text.secondary',
              p: 0.5,
            }}
          >
            <FilterListIcon fontSize="small" />
            {changedFilterCount > 0 && (
              <Box
                component="span"
                sx={{
                  position: 'absolute',
                  top: 0,
                  right: 0,
                  backgroundColor: 'primary.main',
                  color: 'primary.contrastText',
                  borderRadius: '50%',
                  width: 14,
                  height: 14,
                  fontSize: 9,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                }}
              >
                {changedFilterCount}
              </Box>
            )}
          </IconButton>
        </MuiTooltip>
      </Box>

      {/* Status Filter Controls */}
      <Collapse in={showFilters}>
        <Box
          sx={{
            mb: 1,
            px: 1.5,
            py: 1,
            backgroundColor: 'grey.50',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography variant="caption" sx={{ fontWeight: 600, color: 'text.secondary', display: 'block', mb: 0.5 }}>
            Include subjects:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1.5 }}>
            <FormControlLabel
              control={<Checkbox size="small" checked={isCRFPendingIncluded} onChange={handleCRFPendingFilterChange} sx={{ p: 0.25, mr: 0.25 }} />}
              label={<Typography variant="caption">Incomplete Subject forms</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={includeStatuses[SUBJECT_STATUSES.ON_HOLD]} onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.ON_HOLD)} sx={{ p: 0.25, mr: 0.25 }} />}
              label={<Typography variant="caption">On Hold</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={includeStatuses[SUBJECT_STATUSES.SCREENING_FAILURE]} onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.SCREENING_FAILURE)} sx={{ p: 0.25, mr: 0.25 }} />}
              label={<Typography variant="caption">Screening Failed</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={includeStatuses[SUBJECT_STATUSES.ENROLLMENT_FAILURE]} onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.ENROLLMENT_FAILURE)} sx={{ p: 0.25, mr: 0.25 }} />}
              label={<Typography variant="caption">Enrollment Failed</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={includeStatuses[SUBJECT_STATUSES.WITHDRAWAL]} onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.WITHDRAWAL)} sx={{ p: 0.25, mr: 0.25 }} />}
              label={<Typography variant="caption">Withdrawn</Typography>}
              sx={{ m: 0 }}
            />
            <FormControlLabel
              control={<Checkbox size="small" checked={includeStatuses[SUBJECT_STATUSES.COMPLETED]} onChange={() => handleStatusFilterChange(SUBJECT_STATUSES.COMPLETED)} sx={{ p: 0.25, mr: 0.25 }} />}
              label={<Typography variant="caption">Completed</Typography>}
              sx={{ m: 0 }}
            />
          </Box>
        </Box>
      </Collapse>

      {/* Chart Content */}
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', minHeight: 0 }}>
        {isLoading && (
          <Box sx={{ height: '100%', width: '100%', display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box sx={{ height: 250, width: 250 }}>
              <AnimatedBloqcubeLogo />
            </Box>
          </Box>
        )}
        {aggregatedData && <Bar options={options} data={data} />}
      </Box>
    </Box>
  );
};

export default AdvancedTherapiesUsedWidget;
